import { captureException } from "@sentry/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { BackendErrors } from "Domain/Api/Error";
import { flattenObject } from "Utils/Format";

function ErrorMessage({ messages }) {
  console.log(messages);
  const { t } = useTranslation("default");

  const humanReadableErrors = useMemo(
    () => ({
      DUPLICATE_CONTACT: t("default:errors.duplicateContact"),
    }),
    [t],
  );

  return (
    <div>
      {messages.map(([key, value]) => (
        <div key={key}>
          {key && humanReadableErrors[key] ? (
            humanReadableErrors[key]
          ) : (
            <>
              {!!key && <span>{`${key} : `}</span>}
              {value}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

const _parseMessages = (defaultMessage: string, sourceMessage?: BackendErrors) => {
  try {
    if (sourceMessage) {
      const messages = (
        Array.isArray(sourceMessage)
          ? sourceMessage
          : (Object.values(flattenObject(sourceMessage)) as { code: string; message: string }[])
      )
        .map((x: { code: string; message: string }) => [x?.code, x?.message])
        .filter((x) => x.every((y) => y !== undefined));

      if (messages.length) {
        return messages;
      }
    }

    return [["default_error", defaultMessage]];
  } catch (err) {
    captureException(err);

    return [["default_error", defaultMessage]];
  }
};

/**
 * Factory to create an handler spawning an inner error message
 *
 * @param defaultMessage fallback message
 */
ErrorMessage.createErrorHandler = (defaultMessage: string) => async (error) => {
  try {
    const body = Array.isArray(error) ? error : await error?.clone()?.json();
    const messages = _parseMessages(defaultMessage, body);

    toast.error(<ErrorMessage messages={messages} />, {
      position: toast.POSITION.TOP_CENTER,
    });
  } catch (_err) {
    const messages = _parseMessages(defaultMessage);

    toast.error(<ErrorMessage messages={messages} />, {
      position: toast.POSITION.TOP_CENTER,
    });
  }
};

/**
 * Factory to create an handler spawning an inner error message with json already extracted
 *
 * @param defaultMessage fallback message
 */
ErrorMessage.createErrorHandlerWithBody = (defaultMessage: string) => async (body) => {
  const messages = _parseMessages(defaultMessage, Array.isArray(body) ? body : [body]);

  toast.error(<ErrorMessage messages={messages} />, {
    position: toast.POSITION.TOP_CENTER,
  });
};

export default ErrorMessage;
